import {
  CHECKLIST_ITEM_SECTION,
  CHECKLIST_ITEM_TYPE,
} from "constants/financialClosing";
import dayjs from "dayjs";
import {
  getStatus,
  CategorisedIcon,
} from "pages/Books/FinancialClosing/CheckList";
import { ChecklistItem, FinancialClosing } from "store/apis/financialClosing";
import { ArrayElement } from "types/utils/ArrayElement";
import { object, array, ValidationError, mixed } from "yup";

export const financialClosingSchema = (
  checkListItems: ChecklistItem[],
  other_financials: FinancialClosing["other_financials"]
) => {
  return object().shape({
    checklist_items: array().of(
      object().test(
        "checklist-validation",
        "Item must be completed",
        function (item: any) {
          if (!item) return false;

          const status = getStatus({
            resolved_by_system: item.resolved_by_system,
            resolved_by_admin: item.resolved_by_admin,
          }) as keyof typeof CategorisedIcon;

          // For finalize financials, also check file requirements
          if (item.section === CHECKLIST_ITEM_SECTION.FINALIZE_FINANCIALS) {
            const hasFile =
              item.type === CHECKLIST_ITEM_TYPE.REVIEW_CASHFLOW_STATEMENT ||
              Boolean(item.file_url) ||
              Boolean(item.file_data);

            if (!hasFile) {
              return this.createError({
                path: `checklist_items[${checkListItems.findIndex(
                  (i) => i.uuid === item.uuid
                )}]`,
                message: "File is required",
              });
            }

            return true;
          }

          return status === "MANUAL_ACCEPTED" || status === "SYSTEM_SUGGESTED";
        }
      )
    ),
    other_financials: array().of(
      object().test(
        "other-financials-validation",
        "Document name and file/URL are required",
        function (item: any) {
          if (!item) return true; // Empty items are allowed

          const currentIndex = other_financials.findIndex(
            (i: ArrayElement<FinancialClosing["other_financials"]>) =>
              i.uuid === item.uuid
          );

          let errors = [];

          if (!item.document_name) {
            errors.push(
              new ValidationError(
                "Name is required",
                item.document_name,
                `other_financials[${currentIndex}.document_name]`
              )
            );
          }

          if (item.document_type === "UPLOAD" && !item.document) {
            errors.push(
              new ValidationError(
                "File or URL is required",
                item.url,
                `other_financials[${currentIndex}.document]`
              )
            );
          }

          if (item.document_type === "URL" && !item.url) {
            errors.push(
              new ValidationError(
                "File is required",
                item.document,
                `other_financials[${currentIndex}.document]`
              )
            );
          }

          return this.createError({ message: () => errors });
        }
      )
    ),
    accounting_lock_date: mixed().test(
      "date-validation",
      "Date cannot be in the future",
      function (value) {
        if (!value) return true;

        const lockDate = dayjs(value as unknown as string);
        const today = dayjs();

        if (!lockDate) {
          return true;
        }

        if (lockDate.isAfter(today)) {
          return this.createError({
            path: "accounting_lock_date",
            message: "Date cannot be in the future",
          });
        }

        return true;
      }
    ),
  });
};
